import { downloadFile, downloadFiles } from './file-manager';
import { fetch } from './api-wrapper';

// Formats returned as a downloadable file (built client-side); anything else is an
// ERP push that returns the {inserted|error} JSON contract.
const DOWNLOAD_FORMATS = ["acomba", "surepoint", "pdf", "elite_xml", "json"];
// SurePoint returns one combined CSV for the whole selection, so it always uses the
// multi-invoice endpoint — even for a single invoice.
const ALWAYS_MULTI_FORMATS = ["surepoint"];

const MAX_FAILED_SHOWN = 10;

// SurePoint returns 409 {error:"finalize_failed", failed:[{supplier_name, invoice_number,
// reason}]} when some selected invoices can't be finalized. Show the full list so the
// user can fix them and retry; the selection is kept for that retry.
function onExportError(component, error) {
    const t = component.props.data.t;
    const data = error && error.response && error.response.data;
    if (data && data.error === "finalize_failed" && Array.isArray(data.failed)) {
        const capped = data.failed.slice(0, MAX_FAILED_SHOWN);
        const shown = capped.map(f => {
            const who = [f.supplier_name, f.invoice_number].filter(Boolean).join(" ");
            const reason = t.read("export_finalize_reason_" + f.reason) || f.reason;
            return [who, reason && `(${reason})`].filter(Boolean).join(" ");
        }).filter(Boolean);
        if (data.failed.length > capped.length) shown.push(`+${data.failed.length - capped.length}`);
        // Keep the selection so the user can fix the listed invoices and retry the batch.
        component.setState({ errorMessage: t.read("export_finalize_failed") + ": " + shown.join("; "), exporting: new Set() });
    } else {
        // Any other failure: match the original download error path (clear + refresh, no banner).
        component.setState({ exporting: new Set(), invoiceSelection: [] });
    }
    component.refreshInvoices();
}

// Shared export/finalize used by the Archives and Dashboard screens so a format is
// wired in one place. `component` supplies state / setState / refreshInvoices /
// source / props.data.t.
export function exportSelectedInvoices(component, format) {
    component.setState({ exporting: new Set([...component.state.exporting, format]) });
    const ids = [...component.state.invoiceSelection.flatMap(x => x._id)];

    if (!DOWNLOAD_FORMATS.includes(format)) {
        fetch("/invoices/" + format + "/", "GET", { ids },
            response => {
                component.refreshInvoices();
                if (response.data.inserted !== true)
                    component.setState({
                        errorMessage: response.data.error.errorCode + ': ' + response.data.error.errorMessage.value,
                        exporting: new Set(),
                    });
                else
                    component.setState({
                        successMessage: response.data.message,
                        exporting: new Set(),
                        invoiceSelection: [],
                    });
            },
            () => component.setState({ errorMessage: 'erreur inattendue', exporting: new Set() }),
            false, component.source.token);
        return;
    }

    const onDone = () => {
        component.setState({ exporting: new Set(), invoiceSelection: [] });
        component.refreshInvoices();
    };
    if (ids.length > 1 || ALWAYS_MULTI_FORMATS.includes(format))
        downloadFiles(ids, format, onDone, error => onExportError(component, error));
    else
        downloadFile(ids[0], format, onDone);
}

function saveFinalizationFiles(files) {
    (files || []).forEach(file => {
        if (!file.file_string) return;
        const link = document.createElement('a');
        link.href = 'data:attachment/csv,' + encodeURIComponent(file.file_string);
        link.target = '_blank';
        link.download = file.file_name + '.csv';
        document.body.appendChild(link);
        link.click();
        link.remove();
    });
}

export function finalizeSelectedInvoices(component, format) {
    component.setState({ exporting: new Set([...component.state.exporting, format]) });
    const ids = [...component.state.invoiceSelection.flatMap(invoice => invoice._id)];

    fetch('/invoices/finalize', 'POST', { ids, format }, response => {
        if (["acomba", "surepoint", "juris_lexis_nexis"].includes(format)) {
            saveFinalizationFiles(response.data);
            component.setState({ exporting: new Set(), invoiceSelection: [] });
        } else if (response.data.inserted !== true) {
            component.setState({
                errorMessage: response.data.error.errorCode + ': ' + response.data.error.errorMessage.value,
                exporting: new Set(),
            });
        } else {
            component.setState({
                successMessage: response.data.message,
                exporting: new Set(),
                invoiceSelection: [],
            });
        }
        component.refreshInvoices();
    }, error => onExportError(component, error), false, component.source.token);
}
